import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Archive } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import { WPScopeNote } from "./WPStatusBadge";
import { Field, PagesInput, LangToggle, checkCommon } from "./WPFields";
import { wpApi, LANGS, inputCls, btnPrimary, btnGhost, langText, useWP } from "./wpApi";

const EMPTY = { name: "", languages: ["de"], page_scope: "10", goal: "", notes: "" };

function PresetForm({ onSaved }) {
  const { t, err } = useWP();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const submit = async (e) => {
    e.preventDefault();
    const v = !f.name.trim() ? "Bitte einen Namen für die Vorlage angeben." : checkCommon(f);
    if (v) { setError(t(v)); return; }
    setBusy(true); setError(null);
    try {
      await wpApi.createPreset({ name: f.name, languages: LANGS.filter((l) => f.languages.includes(l)), page_scope: Number(f.page_scope), goal: f.goal || null, notes: f.notes || null });
      toast.success(t("Vorlage gespeichert"));
      setF(EMPTY);
      onSaved();
    } catch (ex) { setError(err(ex, "Vorlage konnte nicht gespeichert werden")); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="wp-preset-form">
      <Card className="p-5 sm:p-6 grid md:grid-cols-2 gap-5">
        <Field label={t("Name der Vorlage")} required className="md:col-span-2"><input value={f.name} onChange={set("name")} maxLength={120} className={inputCls} placeholder={t("z. B. KMU Standard DE/FR")} data-testid="wp-preset-name" /></Field>
        <Field label={t("Seitenumfang (1–100)")} required><PagesInput value={f.page_scope} onChange={set("page_scope")} testid="wp-preset-pages" /></Field>
        <Field label={t("Sprachen")} required><LangToggle value={f.languages} onChange={set("languages")} testid="wp-preset-lang" /></Field>
        <Field label={t("Ziel (Vorschlag)")} className="md:col-span-2"><textarea value={f.goal} onChange={set("goal")} rows={2} maxLength={1000} className={inputCls} data-testid="wp-preset-goal" /></Field>
        <Field label={t("Notizen")} className="md:col-span-2"><textarea value={f.notes} onChange={set("notes")} rows={2} maxLength={2000} className={inputCls} data-testid="wp-preset-notes" /></Field>
        {error && <div role="alert" className="md:col-span-2 text-sm text-red-300" data-testid="wp-preset-error">{error}</div>}
        <div className="md:col-span-2"><button type="submit" disabled={busy} className={btnPrimary} data-testid="wp-preset-submit"><Plus className="w-4 h-4" /> {t("Vorlage speichern")}</button></div>
      </Card>
    </form>
  );
}

export default function WPPresets() {
  const { t, err, pages } = useWP();
  const [rows, setRows] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const load = useCallback(() => wpApi.presets({ include_archived: showArchived }).then((r) => setRows(r.data)).catch(() => setRows([])), [showArchived]);
  useEffect(() => { load(); }, [load]);
  const archive = async (p) => {
    try { await wpApi.archivePreset(p.id); toast.success(t("Vorlage archiviert")); load(); } catch (e) { toast.error(err(e, "Archivieren fehlgeschlagen")); }
  };
  return (
    <div data-testid="wp-presets-page" className="max-w-4xl">
      <Link to="/admin/website-production" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-4 transition-colors" data-testid="wp-presets-back"><ArrowLeft className="w-4 h-4" /> {t("Alle Aufträge")}</Link>
      <PageHeader title={t("Projektvorgaben")} subtitle={t("Wiederverwendbare Vorlagen für Sprachen, Seitenumfang und Ziel. Beim Anwenden wird kopiert, nicht verknüpft.")} />
      <WPScopeNote />
      <PresetForm onSaved={load} />
      <div className="mt-8 mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-gray-400">{t("Gespeicherte Vorlagen")}</h2>
        <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#A3E635]" data-testid="wp-presets-show-archived" /> {t("Archivierte zeigen")}</label>
      </div>
      {!rows ? <Spinner /> : rows.length === 0 ? <Card><Empty text={t("Noch keine Vorlagen")} /></Card> : (
        <div className="space-y-2" data-testid="wp-presets-list">
          {rows.map((p) => (
            <Card key={p.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`wp-preset-${p.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-white font-semibold break-words">{p.name}{p.status === "archived" && <span className="ml-2 text-[11px] text-gray-500 uppercase">{t("archiviert")}</span>}</div>
                <div className="text-xs text-gray-500 mt-0.5">{pages(p.page_scope)} · {langText(p.languages)}{p.goal ? ` · ${p.goal}` : ""}</div>
              </div>
              {p.status === "active" && <button onClick={() => archive(p)} className={btnGhost} data-testid={`wp-preset-archive-${p.id}`}><Archive className="w-4 h-4" /> {t("Archivieren")}</button>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
